import React, { useState, useCallback } from 'react';

import {
  EntitySearchModal,
  type EntitySearchOption,
} from '../../../../components/FormComponents';
import { useSupplierSearch, useSupplierLinking } from '../../hooks';

import { SupplierSelectOption } from './utils';

interface LinkSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  onSuccess?: (selectedSupplier: SupplierSelectOption) => void;
}

export const LinkSupplierModal: React.FC<LinkSupplierModalProps> = ({
  isOpen,
  onClose,
  userId,
  userName,
  onSuccess,
}) => {
  const [error, setError] = useState<string | null>(null);

  const {
    isLoading: searchLoading,
    options: searchResults,
    searchSuppliers,
  } = useSupplierSearch();
  const { linkUserToSupplier, isLinking } = useSupplierLinking();

  // Convert SupplierSelectOption to EntitySearchOption format
  const supplierEntities: EntitySearchOption[] = searchResults.map(
    (supplier) => ({
      id: supplier.value,
      name: supplier.label,
      label: supplier.label,
    })
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.trim()) {
        searchSuppliers(searchTerm.trim());
      }
    },
    [searchSuppliers]
  );

  const handleConfirm = async (selectedEntity: EntitySearchOption) => {
    setError(null);

    // Convert back to SupplierSelectOption format
    const supplierOption: SupplierSelectOption = {
      value: selectedEntity.id,
      label: selectedEntity.label,
    };

    try {
      // If we have a non-empty userId, this is an edit flow - call the mutation
      if (userId && userId !== '') {
        await linkUserToSupplier(supplierOption, userId);
      }

      // Always call onSuccess with the selected supplier
      onSuccess?.(supplierOption);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link supplier');
    }
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  const renderConfirmation = (entity: EntitySearchOption) => (
    <div className="text-center">
      <p className="text-lg font-semibold text-foreground">
        Link {userName} to {entity.name}?
      </p>
      <p className="text-sm text-default-500 mt-2">
        This user will be associated with the selected supplier.
      </p>
    </div>
  );

  return (
    <EntitySearchModal
      isOpen={isOpen}
      onClose={onClose}
      title="Link User to Supplier"
      searchPlaceholder="Search suppliers by name or reference ID..."
      entityType="supplier"
      isSearching={searchLoading}
      searchResults={supplierEntities}
      onSearch={handleSearch}
      onConfirm={handleConfirm}
      isConfirming={isLinking}
      renderConfirmation={renderConfirmation}
      error={error}
      onErrorDismiss={handleErrorDismiss}
    />
  );
};
